/**
 * Operational Governance Hub - Main Landing Page
 * =============================================
 * Enterprise-grade operational governance infrastructure
 * for PHS controlled commercial pilot rollout.
 * 
 * Route: /wyldecrest/helpdesk/governance
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PHSCommandLayout } from '@/components/phs/enterprise';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, 
  Users, 
  FileCheck, 
  DollarSign, 
  AlertTriangle,
  UserPlus,
  Rocket,
  Server,
  Search,
  ChevronRight,
  Clock,
  CheckCircle,
  Printer
} from 'lucide-react';

// Governance document categories
const governanceCategories = [
  {
    id: 'sops',
    title: 'Internal SOP Library',
    description: 'Standard Operating Procedures for PHS internal operations',
    icon: BookOpen,
    path: '/wyldecrest/helpdesk/sops',
    color: 'emerald',
    docCount: 12,
    lastReviewed: '2026-05-20',
    status: 'current'
  },
  {
    id: 'contractor-standards',
    title: 'Contractor SOP Library',
    description: 'Standards and procedures for contractor engagement',
    icon: Users,
    path: '/wyldecrest/helpdesk/contractor-standards',
    color: 'blue',
    docCount: 8,
    lastReviewed: '2026-05-18',
    status: 'current'
  },
  {
    id: 'evidence-governance',
    title: 'Evidence Governance',
    description: 'Evidence collection, validation, and audit standards',
    icon: FileCheck,
    path: '/wyldecrest/helpdesk/evidence-governance',
    color: 'purple',
    docCount: 6,
    lastReviewed: '2026-05-22',
    status: 'current'
  },
  {
    id: 'finance-governance',
    title: 'Finance & PO Governance',
    description: 'Purchase order, invoicing, and financial controls',
    icon: DollarSign,
    path: '/wyldecrest/helpdesk/finance-governance',
    color: 'amber',
    docCount: 10,
    lastReviewed: '2026-05-21',
    status: 'current'
  },
  {
    id: 'incident-management',
    title: 'Incident Management',
    description: 'Issue escalation, incident response, and resolution protocols',
    icon: AlertTriangle,
    path: '/wyldecrest/helpdesk/incident-management',
    color: 'red',
    docCount: 5,
    lastReviewed: '2026-05-19',
    status: 'current'
  },
  {
    id: 'client-onboarding',
    title: 'Client Onboarding',
    description: 'New client setup, configuration, and go-live procedures',
    icon: UserPlus,
    path: '/wyldecrest/helpdesk/client-onboarding',
    color: 'teal',
    docCount: 7,
    lastReviewed: '2026-05-23',
    status: 'current'
  },
  {
    id: 'pilot-rollout',
    title: 'Pilot Rollout Guidance',
    description: 'Controlled commercial pilot deployment procedures',
    icon: Rocket,
    path: '/wyldecrest/helpdesk/pilot-rollout',
    color: 'indigo',
    docCount: 4,
    lastReviewed: '2026-05-23',
    status: 'current'
  },
  {
    id: 'deployment-governance',
    title: 'Deployment Governance',
    description: 'Release management, deployment, and rollback procedures',
    icon: Server,
    path: '/wyldecrest/helpdesk/deployment-governance',
    color: 'slate',
    docCount: 6,
    lastReviewed: '2026-05-23',
    status: 'current'
  },
  {
    id: 'user-guide',
    title: 'Helpdesk User Guide',
    description: 'Complete operational manual for helpdesk staff',
    icon: BookOpen,
    path: '/wyldecrest/helpdesk/user-guide',
    color: 'cyan',
    docCount: 20,
    lastReviewed: '2026-05-23',
    status: 'current'
  }
];

// Color mapping for cards
const colorMap = {
  emerald: {
    bg: 'bg-emerald-900/30',
    border: 'border-emerald-700/50',
    icon: 'text-emerald-400',
    badge: 'bg-emerald-500/20 text-emerald-300'
  },
  blue: {
    bg: 'bg-blue-900/30',
    border: 'border-blue-700/50',
    icon: 'text-blue-400',
    badge: 'bg-blue-500/20 text-blue-300'
  },
  purple: {
    bg: 'bg-purple-900/30',
    border: 'border-purple-700/50',
    icon: 'text-purple-400',
    badge: 'bg-purple-500/20 text-purple-300'
  },
  amber: {
    bg: 'bg-amber-900/30',
    border: 'border-amber-700/50',
    icon: 'text-amber-400',
    badge: 'bg-amber-500/20 text-amber-300'
  },
  red: {
    bg: 'bg-red-900/30',
    border: 'border-red-700/50',
    icon: 'text-red-400',
    badge: 'bg-red-500/20 text-red-300'
  },
  teal: {
    bg: 'bg-teal-900/30',
    border: 'border-teal-700/50',
    icon: 'text-teal-400',
    badge: 'bg-teal-500/20 text-teal-300'
  },
  indigo: {
    bg: 'bg-indigo-900/30',
    border: 'border-indigo-700/50',
    icon: 'text-indigo-400',
    badge: 'bg-indigo-500/20 text-indigo-300'
  },
  slate: {
    bg: 'bg-slate-800/50',
    border: 'border-slate-600/50',
    icon: 'text-slate-400',
    badge: 'bg-slate-500/20 text-slate-300'
  },
  cyan: {
    bg: 'bg-cyan-900/30',
    border: 'border-cyan-700/50',
    icon: 'text-cyan-400',
    badge: 'bg-cyan-500/20 text-cyan-300'
  }
};

export default function GovernanceHub() {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCategories = governanceCategories.filter(cat =>
    cat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cat.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <PHSCommandLayout>
      <div className="min-h-screen bg-[#0f1a19]" data-testid="governance-hub">
        {/* Header */}
        <div className="border-b border-slate-700/50 bg-[#162624] px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                  <BookOpen className="h-7 w-7 text-emerald-400" />
                  Operational Governance
                </h1>
                <p className="text-slate-400 mt-1">
                  Enterprise documentation and operational standards
                </p>
              </div>
              
              {/* Search */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="text"
                    placeholder="Search documentation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 w-64"
                    data-testid="governance-search"
                  />
                </div>
                <button
                  onClick={() => window.print()}
                  className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 transition"
                  title="Print Documentation"
                >
                  <Printer className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Version Info */}
            <div className="flex items-center gap-4 mt-4 text-sm">
              <span className="text-slate-500">
                Version: <span className="text-emerald-400 font-medium">2.0.0-RC1</span>
              </span>
              <span className="text-slate-600">|</span>
              <span className="text-slate-500">
                Last Updated: <span className="text-slate-300">May 23, 2026</span>
              </span>
              <span className="text-slate-600">|</span>
              <span className="flex items-center gap-1 text-emerald-400">
                <CheckCircle className="h-4 w-4" />
                All Documents Current
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
              <div className="text-3xl font-bold text-white">9</div>
              <div className="text-sm text-slate-400">Document Categories</div>
            </div>
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
              <div className="text-3xl font-bold text-emerald-400">78</div>
              <div className="text-sm text-slate-400">Total Documents</div>
            </div>
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
              <div className="text-3xl font-bold text-blue-400">100%</div>
              <div className="text-sm text-slate-400">Documents Reviewed</div>
            </div>
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
              <div className="text-3xl font-bold text-amber-400">0</div>
              <div className="text-sm text-slate-400">Pending Updates</div>
            </div>
          </div>

          {/* Category Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCategories.map((category) => {
              const Icon = category.icon;
              const colors = colorMap[category.color];
              
              return (
                <Link
                  key={category.id}
                  to={category.path}
                  className={`block ${colors.bg} ${colors.border} border rounded-xl p-5 hover:scale-[1.02] transition-all duration-200 group`}
                  data-testid={`governance-card-${category.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2.5 rounded-lg bg-slate-900/50 ${colors.icon}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <Badge variant="outline" className={colors.badge}>
                      {category.docCount} docs
                    </Badge>
                  </div>
                  
                  <h3 className="text-white font-semibold mb-1 group-hover:text-emerald-300 transition">
                    {category.title}
                  </h3>
                  <p className="text-slate-400 text-sm mb-3 line-clamp-2">
                    {category.description}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1 text-slate-500">
                      <Clock className="h-3 w-3" />
                      {category.lastReviewed}
                    </span>
                    <span className="flex items-center gap-1 text-emerald-400 group-hover:translate-x-1 transition-transform">
                      View <ChevronRight className="h-3 w-3" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* No Results */}
          {filteredCategories.length === 0 && (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No documents found</h3>
              <p className="text-slate-400">Try adjusting your search query</p>
            </div>
          )}
        </div>

        {/* Print Styles */}
        <style>{`
          @media print {
            body { background: white !important; }
            .bg-\\[\\#0f1a19\\] { background: white !important; }
            .bg-\\[\\#162624\\] { background: #f3f4f6 !important; }
            .text-white { color: black !important; }
            .text-slate-400 { color: #6b7280 !important; }
            .text-emerald-400 { color: #059669 !important; }
            button { display: none !important; }
          }
        `}</style>
      </div>
    </PHSCommandLayout>
  );
}
